/**
 * Sidebar information architecture, taken from the approved designs
 * (workspace-dashboard.html and the 102 other shell pages).
 *
 * Each item declares the permission that reveals it — `permission` is checked
 * against the signed-in user's grants, so the nav shows only what they can
 * actually open. The API still enforces the real check (FR-35).
 */

/** Icon keys resolved to SVGs in Sidebar's NAV_ICONS map. */
export type NavIcon =
  | 'dashboard'
  | 'ai'
  | 'aiAssistant'
  | 'projects'
  | 'tasks'
  | 'calendar'
  | 'crm'
  | 'deals'
  | 'pipeline'
  | 'territory'
  | 'sequences'
  | 'quotations'
  | 'hrms'
  | 'finance'
  | 'documents'
  | 'assets'
  | 'inventory'
  | 'procurement'
  | 'generalLedger'
  | 'reports'
  | 'workflow'
  | 'settings'
  | 'users'
  | 'roles'
  | 'billing'
  | 'audit'
  | 'portal'
  | 'inbox'
  | 'whatsapp'
  | 'agency'
  | 'marketplace'
  | 'holidays'
  | 'attendance'
  | 'leave'
  | 'remoteWork'
  | 'payslips';

export interface NavItem {
  label: string;
  to: string;
  /** Icon shown to the left of the label in the dark rail. */
  icon: NavIcon;
  /** Any-of: the item shows if the user holds at least one. */
  permission?: string[];
  /** Exact-match the route for the active highlight (for prefix-shared paths). */
  end?: boolean;
  /** Marks routes not yet implemented, so the shell can show them as upcoming. */
  planned?: boolean;
}

export interface NavSection {
  /** Section label in the sidebar; the designs group nav under these. */
  title: string;
  /** Any-of: the whole section shows only for a user holding one of these roles. */
  roles?: string[];
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    title: 'General',
    items: [
      { label: 'Dashboard', to: '/', icon: 'dashboard' },
      { label: 'Executive Dashboard', to: '/dashboard/executive', icon: 'dashboard', permission: ['report:read'] },
      { label: 'AI Employees', to: '/ai-employees', icon: 'ai', permission: ['aiEmployee:read'] },
      { label: 'AI Assistant', to: '/ai-assistant', icon: 'aiAssistant', permission: ['aiAssistant:read'] },
      { label: 'Projects', to: '/projects', icon: 'projects', permission: ['project:read'] },
      { label: 'Tasks', to: '/tasks', icon: 'tasks', permission: ['task:read'] },
      { label: 'Calendar', to: '/calendar', icon: 'calendar', permission: ['event:read'] },
      // No permission — self-serve for every authenticated user, matching
      // the backend (GET /assets/mine has no authorize() gate).
      { label: 'My Assets', to: '/assets/mine', icon: 'assets' },
    ],
  },
  {
    title: 'Revenue',
    items: [
      { label: 'CRM · Leads', to: '/crm', icon: 'crm', permission: ['lead:read'], end: true },
      { label: 'Deals', to: '/crm/deals', icon: 'deals', permission: ['deal:read'] },
      { label: 'Pipelines', to: '/crm/pipelines', icon: 'pipeline', permission: ['pipeline:read'] },
      { label: 'Territories', to: '/crm/territories', icon: 'territory', permission: ['territory:read'] },
      { label: 'Services', to: '/crm/services', icon: 'territory', permission: ['service:read'] },
      { label: 'Sequences', to: '/crm/sequences', icon: 'sequences', permission: ['sequence:read'] },
      { label: 'Quotations', to: '/quotations', icon: 'quotations', permission: ['quote:read'] },
      { label: 'Inbox', to: '/inbox', icon: 'inbox', permission: ['mailbox:read'] },
      { label: 'WhatsApp', to: '/whatsapp', icon: 'whatsapp', permission: ['whatsapp:read'] },
      { label: 'WhatsApp Campaigns', to: '/whatsapp/campaigns', icon: 'whatsapp', permission: ['whatsapp:read'] },
      { label: 'Retainers', to: '/agency/retainers', icon: 'agency', permission: ['retainer:read'] },
      { label: 'Marketing Campaigns', to: '/agency/campaigns', icon: 'agency', permission: ['marketingCampaign:read'] },
    ],
  },
  {
    title: 'Organization',
    items: [
      { label: 'HRMS', to: '/hrms', icon: 'hrms', permission: ['employee:read'] },
      {
        label: 'Finance',
        to: '/finance',
        icon: 'finance',
        // Merged section (Overview/Chart of Accounts/General Ledger/Financial
        // Statements/Tax & Compliance/Budgets & Forecasting) — any one tab's
        // read permission is enough to see the nav item; FinanceLayout hides
        // the tabs the user can't open.
        permission: ['invoice:read', 'ledgerAccount:read', 'journalEntry:read', 'taxFiling:read', 'budgetLine:read'],
      },
      { label: 'Documents', to: '/documents', icon: 'documents', permission: ['file:read'] },
      { label: 'Assets', to: '/assets', icon: 'assets', permission: ['asset:read'] },
      { label: 'Inventory', to: '/inventory', icon: 'inventory', permission: ['product:read'] },
      { label: 'Procurement', to: '/procurement', icon: 'procurement', permission: ['vendor:read', 'purchaseRequest:read', 'purchaseOrder:read', 'rfq:read'] },
      { label: 'Reports', to: '/reports', icon: 'reports', permission: ['report:read'], end: true },
      { label: 'Project & Task Reports', to: '/reports/projects-tasks', icon: 'reports', permission: ['report:read'] },
      { label: 'Workflow Engine', to: '/workflow', icon: 'workflow', permission: ['automation:read'] },
      { label: 'Marketplace', to: '/marketplace', icon: 'marketplace', permission: ['pluginInstallation:read'] },
      { label: 'Installed Plugins', to: '/marketplace/installed', icon: 'marketplace', permission: ['pluginInstallation:read'] },
      { label: 'Developer Portal', to: '/developer-portal', icon: 'marketplace', permission: ['marketplaceApp:read'] },
    ],
  },
  {
    // Fixed for the Employee role — own HR self-service, regardless of
    // whatever else the role's other permissions surface above.
    title: 'Employee',
    roles: ['employee'],
    items: [
      { label: 'Holidays', to: '/holidays', icon: 'holidays', permission: ['holiday:read'] },
      { label: 'Attendance', to: '/attendance/mine', icon: 'attendance' },
      { label: 'Leave', to: '/leave/mine', icon: 'leave' },
      { label: 'Remote Work', to: '/remote-work/mine', icon: 'remoteWork' },
      { label: 'Payslips', to: '/payslips/mine', icon: 'payslips' },
    ],
  },
  {
    title: 'Admin',
    items: [
      { label: 'Settings', to: '/settings', icon: 'settings', permission: ['setting:read'] },
      { label: 'Portal Access', to: '/settings/portal-access', icon: 'portal', permission: ['user:read'] },
      { label: 'Portal Tickets', to: '/portal-tickets', icon: 'inbox', permission: ['portalTicket:read'] },
      { label: 'Calendar Sync', to: '/settings/calendar-integrations', icon: 'calendar', permission: ['event:read'] },
      { label: 'Lead Sources', to: '/settings/lead-sources', icon: 'inbox', permission: ['lead:read'] },
      { label: 'Lead Options', to: '/settings/lead-options', icon: 'inbox', permission: ['lead:read'] },
      { label: 'Country / State / City', to: '/settings/lead-geo-options', icon: 'territory', permission: ['lead:read'] },
      { label: 'Line Item Descriptions', to: '/settings/line-item-descriptions', icon: 'inbox', permission: ['setting:read'] },
      { label: 'Webhook Endpoints', to: '/settings/webhooks', icon: 'inbox', permission: ['webhook:read'] },
      { label: 'Mailbox Connections', to: '/settings/mailboxes', icon: 'inbox', permission: ['mailbox:manage'] },
      { label: 'WhatsApp Numbers', to: '/settings/whatsapp-numbers', icon: 'whatsapp', permission: ['whatsapp:manage'] },
      { label: 'Users', to: '/users', icon: 'users', permission: ['user:read'] },
      { label: 'Roles', to: '/roles', icon: 'roles', permission: ['role:read'] },
      { label: 'Billing', to: '/billing', icon: 'billing', permission: ['billing:read'] },
      { label: 'Audit Log', to: '/audit', icon: 'audit', permission: ['audit:read'] },
      // No permission — self-serve for every authenticated user.
      { label: 'Compliance & Data', to: '/settings/compliance', icon: 'audit' },
    ],
  },
];
